import {
  LinearProgress,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  Menu,
  MenuItem,
  Chip,
  Box,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setOpen } from "../../Redux/ModalReducer";
import { api_Routes } from "../../tools/api_Routes";
import { Helper } from "../../tools/Helper";
import DataTable from "./data-table";

export const UserAdsTable = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [show, setShow] = useState(false);
  const [selectedId, setSelectedId] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: 'id',
    direction: 'desc'
  });
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const dispatch = useDispatch();

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleShow = () => {
    setShow(true);
    dispatch(setOpen(true));
  };

  const handleClose = () => {
    setShow(false);
    dispatch(setOpen(false));
  };

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
  };

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [keyword]);

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(0);
  };

  const handleSelectionChange = (newSelected, newSelectAllMode) => {
    setSelected(newSelected);
    setSelectAllMode(newSelectAllMode);
  };

  const handleBulkAction = async (action) => {
    if (!selected.size && !selectAllMode) return;

    setIsLoading(true);
    try {
      const ids = selectAllMode
          ? 'ALL'
          : Array.from(selected);

      const payload = {
        action,
        ids,
        filters: selectAllMode ? { keyword: debouncedKeyword } : null
      };

      const { response } = await Helper.Post({
        url: api_Routes.UserAds.BulkActions,
        data: payload,
        hasToken: true
      });

      if (response) {
        await getData();
        setSelected(new Set());
        setSelectAllMode(false);
      }
    } catch (error) {
      console.error("Bulk action failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getData = async () => {
    setIsLoading(true);

    const queryParams = [];

    // Add search parameter
    if (debouncedKeyword) {
      queryParams.push(`keyword=${debouncedKeyword}`);
    }

    // Add sorting parameters
    queryParams.push(`sort_by=${sortConfig.field}`);
    queryParams.push(`sort_order=${sortConfig.direction}`);

    const { response, message } = await Helper.Get({
      url: `${api_Routes.UserAds.View}?per_page=${rowsPerPage}&page=${
          currentPage + 1
      }${queryParams.length > 0 ? `&${queryParams.join('&')}` : ''}`,
      hasToken: true,
    });
    if (response) {
      setData(response);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (shouldRefresh) {
      setCurrentPage(0);
      setShouldRefresh(false);
      getData();
    }
  }, [shouldRefresh]);

  useEffect(() => {
    getData();
  }, [currentPage, rowsPerPage, debouncedKeyword, sortConfig]);

  return (
      <div>
        {/* Filter Section */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-4 items-center mb-3">
            <label className="font-bold inline-block text-textColor dark:text-darkTextColor text-[15px] text-start">
              Search :
              <input
                  placeholder="Search by title, address, or category"
                  type="text"
                  name="keyword"
                  value={keyword}
                  onChange={handleKeywordChange}
                  autoComplete="off"
                  className="duration-200 font-normal bg-secondary dark:bg-darkPrimary text-textColor dark:text-darkTextColor ml-2
                border-2 input-box rounded-[0.25rem] py-1 px-3 outline-none"
              />
            </label>

            {debouncedKeyword && (
                <Chip
                    label={`Search: ${debouncedKeyword}`}
                    onDelete={() => setKeyword("")}
                />
            )}
          </div>
        </div>

        {/* Bulk Actions */}
        {(selected.size > 0 || selectAllMode) && (
            <div className="bg-gray-100 p-3 mb-3 rounded flex items-center">
          <span className="mr-4">
            {selectAllMode
                ? `All ${data?.pagination?.total || 0} selected (filtered)`
                : `${selected.size} selected`}
          </span>

              <div className="relative">
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endIcon={<span className="ml-1">▼</span>}
                    className="mr-2"
                >
                  Bulk Actions
                </Button>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                  <MenuItem onClick={() => {
                    handleBulkAction('activate');
                    setAnchorEl(null);
                  }}>
                    Activate Selected
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('deactivate');
                    setAnchorEl(null);
                  }}>
                    Deactivate Selected
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('complete');
                    setAnchorEl(null);
                  }}>
                    Mark as Complete
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleBulkAction('cancel');
                    setAnchorEl(null);
                  }}>
                    Cancel Selected
                  </MenuItem>
                </Menu>
              </div>

              <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelected(new Set());
                    setSelectAllMode(false);
                  }}
              >
                Clear
              </Button>
            </div>
        )}

        {/* Table and Pagination */}
        {isLoading ? (
            <LinearProgress sx={{ marginTop: 1 }} />
        ) : data && data?.data?.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <DataTable
                    data={data?.data}
                    getData={getData}
                    selected={selected}
                    selectAllMode={selectAllMode}
                    onSelectionChange={handleSelectionChange}
                    totalCount={data?.pagination?.total}
                    page={currentPage}
                    rowsPerPage={rowsPerPage}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
              </div>
              <div className="mt-4 flex justify-center items-center">
                <TablePagination
                    page={currentPage}
                    className="mt-auto"
                    onPageChange={handlePageChange}
                    count={data.pagination.total}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    variant="outlined"
                    shape="rounded"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </div>
            </>
        ) : (
            <TableContainer>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "primary.main",
                          color: "white",
                          fontWeight: "bold",
                        }}
                    >
                      There are no data yet
                    </TableCell>
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
        )}
      </div>
  );
};